/**
 * Combat, casting and mob AI.
 *
 * Ported from `L2-MECANICAS/src/game/CombatEngine.ts`, with every formula and
 * constant kept and the machinery around them rebuilt:
 *
 *  - everything is per second, not per frame. The original moved characters by
 *    `0.18` units per `requestAnimationFrame`, which made a 144 Hz monitor a
 *    2.4× speed hack.
 *  - cooldowns, cast times and ranges are enforced. The original stored all
 *    three on every skill and checked none of them.
 *  - buffs and debuffs exist, mobs leash back to their spawn and forget you,
 *    death is a state you have to be revived from, and quests can be turned in.
 */

import { gameState } from "./gameState.js";
import { SKILLS, MOBS, QUESTS, ITEMS } from "./worldData.js";
import { seek, halt, distanceTo, angleTo } from "./nav.js";
import { playSkillVfx, releaseHeldVfx } from "./skillVfx.js";
import { audio } from "./audio.js";
import { S } from "../core/settings.js";

/** Melee reach, metres. The original's 2.5 "units" on a 240-unit map. */
const MELEE_RANGE = 2.4;
/** How close a mob gets before it swings. */
const MOB_RANGE = 2.2;
/** Aggro radius for hostile mobs. */
const AGGRO_RANGE = 12;
/** Distance from spawn at which a mob gives up and walks home. */
const LEASH_RANGE = 38;
/** Fixed mob swing interval, seconds. */
const MOB_ATTACK_INTERVAL = 1.8;
/** Corpse timer, seconds. */
const RESPAWN_DELAY = 8;
/** Regeneration tick, seconds. */
const REGEN_INTERVAL = 3;

const COLOR_NORMAL = "#ffffff";
const COLOR_CRIT = "#facc15";
const COLOR_SOULSHOT = "#38bdf8";
const COLOR_SKILL = "#60a5fa";
const COLOR_HURT = "#ef4444";
const COLOR_HEAL = "#22c55e";

export class Combat {
    /** @param {import("./game.js").Game} game */
    constructor(game) {
        this.game = game;
        this.state = gameState;

        /** @type {import("./actors.js").MobActor|null} */
        this.target = null;
        this.autoAttack = false;

        /** @type {{skill:object, target:any, elapsed:number, total:number}|null} */
        this.cast = null;
        /** Seconds remaining, keyed by skill id. */
        this.cooldowns = new Map();

        this.attackTimer = 0;
        this.regenTimer = 0;
        this.deathTimer = 0;
    }

    // =============================================================== frame
    /** @param {number} dt */
    tick(dt) {
        const st = this.state;

        if (st.expireBuffs()) st.notify();
        this._tickCooldowns(dt);

        if (st.isDead) {
            this.deathTimer += dt;
            halt(this.game.player);
            this._tickMobs(dt);
            return;
        }

        this._tickCast(dt);
        this._tickAutoAttack(dt);
        this._tickMobs(dt);
        this._tickRegen(dt);
        st.pruneFloatingTexts();
    }

    _tickCooldowns(dt) {
        for (const [id, left] of this.cooldowns) {
            const next = left - dt;
            if (next <= 0) this.cooldowns.delete(id);
            else this.cooldowns.set(id, next);
        }
    }

    cooldownLeft(skillId) {
        return this.cooldowns.get(skillId) ?? 0;
    }

    // ============================================================ targeting
    /** @param {import("./actors.js").MobActor|import("./actors.js").NpcActor|null} actor */
    setTarget(actor) {
        this.target = actor;
        this.state.target = actor ? { id: actor.id, type: actor.kind } : null;
        if (!actor || actor.kind !== "mob") this.autoAttack = false;
        this.state.notify();
    }

    clearTarget() {
        this.setTarget(null);
    }

    /** Begin swinging at the current target. */
    engage() {
        if (!this.target || this.target.kind !== "mob") {
            this.state.system("No target selected.");
            return;
        }
        if (this.target.isDead) return;
        this.autoAttack = true;
        this.target.aggro = true;
        this.target.state = "chase";
    }

    disengage() {
        this.autoAttack = false;
    }

    // =============================================================== casting
    /**
     * Start a skill.
     *
     * The whole gate is here: known skill, alive, not already casting, off
     * cooldown, enough MP, a legal target, and in range. The original checked
     * only the MP.
     *
     * @param {string} skillId
     */
    castSkill(skillId) {
        const st = this.state;
        const skill = SKILLS[skillId];
        if (!skill || st.isDead) return false;

        if (skillId === "attack") {
            this.engage();
            return true;
        }
        if (skillId === "soulshot_toggle") {
            st.toggleSoulshot();
            return true;
        }
        if (this.cast) {
            st.system("Already casting.");
            return false;
        }
        const cd = this.cooldownLeft(skillId);
        if (cd > 0) {
            st.system(`${skill.name} is not ready (${cd.toFixed(1)}s).`);
            return false;
        }
        if (st.stats.mp < skill.mpCost) {
            st.system("Not enough MP.");
            return false;
        }

        const target = this._resolveCastTarget(skill);
        if (target === undefined) return false;

        if (target && skill.range > 0) {
            const dist = distanceTo(this.game.player, target.position.x, target.position.z);
            if (dist > skill.range + 0.6) {
                st.system(`${skill.name} is out of range.`);
                return false;
            }
        }

        st.stats.mp -= skill.mpCost;
        this.cast = { skill, target, elapsed: 0, total: skill.castTime, mpSpent: skill.mpCost };
        // Casting roots you, as it does in Lineage. Doing this here rather than
        // letting the walk run out means the cast is not racing the character's
        // own deceleration.
        this.game.stopMoving();
        // A held effect starts with the cast so the water is already in the air
        // when the damage lands.
        if (skill.vfx === "ribbon") playSkillVfx(this.game.spells, "ribbon", this._vfxWhere(target));
        if (skill.castTime <= 0) this._completeCast();
        st.notify();
        return true;
    }

    /**
     * Who a skill is aimed at.
     * @returns {any|null|undefined} an actor, null for a self-cast, or
     *   `undefined` to mean the cast was refused.
     */
    _resolveCastTarget(skill) {
        if (!skill.damageMultiplier) return null; // heals and buffs are self-cast

        const hostile = this.target && this.target.kind === "mob" && !this.target.isDead
            ? this.target
            : null;
        if (!hostile) {
            this.state.system(`${skill.name} needs a target.`);
            return undefined;
        }
        return hostile;
    }

    _vfxWhere(target) {
        const p = this.game.player.position;
        return {
            origin: { x: p.x, y: p.y, z: p.z },
            target: target
                ? { x: target.position.x, y: target.position.y, z: target.position.z }
                : null,
        };
    }

    _tickCast(dt) {
        if (!this.cast) return;
        this.cast.elapsed += dt;
        if (this.cast.elapsed >= this.cast.total) this._completeCast();
    }

    /**
     * Abandon the cast in progress.
     *
     * Called when the player gives a move order, and on death. The mana comes
     * back: the cast is interrupted by something the player did on purpose, and
     * charging for a spell that never went off reads as a bug even when it is a
     * rule.
     */
    cancelCast(reason) {
        if (!this.cast) return;
        const { mpSpent } = this.cast;
        const s = this.state.stats;
        s.mp = Math.min(s.maxMp, s.mp + (mpSpent ?? 0));
        releaseHeldVfx(this.game.spells);
        this.cast = null;
        if (reason) this.state.system(reason);
        this.state.notify();
    }

    _completeCast() {
        const { skill, target } = this.cast;
        const st = this.state;
        this.cast = null;

        this.cooldowns.set(skill.id, skill.cooldown);
        releaseHeldVfx(this.game.spells);
        if (skill.vfx && skill.vfx !== "ribbon") {
            playSkillVfx(this.game.spells, skill.vfx, this._vfxWhere(target));
        }

        const p = this.game.player.position;

        if (skill.healAmount) {
            const healed = Math.min(st.stats.maxHp - st.stats.hp, skill.healAmount);
            st.stats.hp += healed;
            audio.playMagicCast(true);
            st.addFloatingText(`+${healed} HP`, p.x, p.y + 2.1, p.z, COLOR_HEAL);
            st.system(`${skill.name} restores ${healed} HP.`);
        }

        if (skill.buff) {
            st.addBuff(skill.buff);
            audio.playMagicCast(true);
            st.addFloatingText(skill.name, p.x, p.y + 2.4, p.z, COLOR_CRIT, true);
        }

        if (skill.damageMultiplier && target && !target.isDead) {
            this._skillDamage(skill, target);
        }
        st.notify();
    }

    _skillDamage(skill, mob) {
        const st = this.state;
        const level = st.skillLevel(skill.id);
        // Skill levels earned with SP finally do something: +8% per level.
        const mastery = 1 + (level - 1) * 0.08;

        const power = skill.physical ? st.stats.patk : st.stats.matk;
        let damage = Math.max(15, power * skill.damageMultiplier * 2 * mastery - mob.data.pdef);
        damage = Math.floor(damage);

        audio.playMagicCast(false);
        this._applyDamage(mob, damage, `${skill.name}! ${damage}`, COLOR_SKILL, true);

        if (skill.debuff && !mob.isDead) {
            const existing = mob.debuffs.find((d) => d.id === skill.debuff.id);
            const until = performance.now() + skill.debuff.duration * 1000;
            if (existing) existing.until = until;
            else mob.debuffs.push({ id: skill.debuff.id, until, mods: skill.debuff.mods });
        }
        if (skill.knockback && !mob.isDead) {
            const a = angleTo(this.game.player, mob.position.x, mob.position.z);
            mob.controller.position.x += Math.sin(a) * skill.knockback;
            mob.controller.position.z += Math.cos(a) * skill.knockback;
        }
    }

    /** 0..1 for the cast bar, or null. */
    castProgress() {
        if (!this.cast) return null;
        if (this.cast.total <= 0) return { name: this.cast.skill.name, t: 1 };
        return { name: this.cast.skill.name, t: this.cast.elapsed / this.cast.total };
    }

    // ========================================================= auto-attack
    _tickAutoAttack(dt) {
        this.attackTimer -= dt;
        // A cast holds everything else still: no chasing, no swinging.
        if (!this.autoAttack || this.cast) return;

        const mob = this.target;
        if (!mob || mob.kind !== "mob" || mob.isDead) {
            this.autoAttack = false;
            return;
        }

        const player = this.game.player;
        const dist = distanceTo(player, mob.position.x, mob.position.z);
        player.intent.faceAngle = angleTo(player, mob.position.x, mob.position.z);

        if (dist > MELEE_RANGE) {
            // Walk in. This overrides the standing move order, and giving a new
            // one is what breaks off the attack.
            if (this.state.isRooted) halt(player);
            else {
                this.game.destination = null;
                seek(player, mob.position.x, mob.position.z,
                    this.state.stats.moveSpeed, MELEE_RANGE - 0.4);
            }
            return;
        }
        halt(player);

        if (this.attackTimer > 0) return;
        const st = this.state;
        // atkSpd 320 → 3.2 swings a second, as in the original.
        this.attackTimer = 1 / Math.max(0.1, st.stats.atkSpd / 100);
        this._physicalAttack(mob);
    }

    _physicalAttack(mob) {
        const st = this.state;
        let damage = Math.max(10, st.stats.patk * 2.2 - mob.data.pdef);

        // Soulshots come out of the bag now. The original kept a separate
        // counter that the soulshot drops never replenished.
        let soulshot = false;
        if (st.stats.soulshotActive && st.consumeItem("soulshot_ng", 1)) {
            damage *= 2;
            soulshot = true;
        }
        st.stats.soulshots = st.findStack("soulshot_ng")?.quantity ?? 0;

        const crit = Math.random() * 100 < st.stats.critRate;
        if (crit) damage *= 2.5;
        damage = Math.floor(damage * (0.9 + Math.random() * 0.2));

        audio.playAttackHit(crit, soulshot);
        const color = crit ? COLOR_CRIT : soulshot ? COLOR_SOULSHOT : COLOR_NORMAL;
        this._applyDamage(mob, damage, crit ? `CRITICAL! ${damage}` : String(damage), color, crit);

        mob.aggro = true;
        if (!mob.isDead) mob.state = "chase";
    }

    _applyDamage(mob, damage, label, color, big) {
        const st = this.state;
        mob.hp -= damage;
        st.addFloatingText(label, mob.position.x, mob.headY, mob.position.z, color, big);
        if (mob.hp <= 0) this._killMob(mob);
        st.notify();
    }

    // ================================================================ kills
    _killMob(mob) {
        const st = this.state;
        const template = MOBS[mob.mobId];
        mob.hp = 0;
        mob.isDead = true;
        mob.state = "dead";
        mob.aggro = false;
        mob.respawnIn = RESPAWN_DELAY;
        mob.debuffs.length = 0;
        this.autoAttack = false;
        this.game.actors.release(mob);

        const adena = Math.floor(
            template.adenaMin + Math.random() * (template.adenaMax - template.adenaMin)
        );
        st.stats.adena += adena;
        st.addChat("System", "system", `${mob.name} defeated. +${adena} adena.`, true);

        for (const drop of template.drops) {
            if (Math.random() >= drop.chance) continue;
            const qty = Math.floor(drop.min + Math.random() * (drop.max - drop.min + 1));
            st.addItem(drop.itemId, qty);
            const item = ITEMS[drop.itemId];
            if (item) {
                st.addChat("System", "system", `Looted ${item.name} ×${qty}.`, true);
            }
        }

        if (st.creditKill(mob.mobId)) st.saveState();

        const levelled = st.addExp(template.expReward, template.spReward);
        if (levelled) {
            audio.playLevelUp();
            const p = this.game.player.position;
            st.addFloatingText("LEVEL UP!", p.x, p.y + 2.6, p.z, COLOR_CRIT, true);
        }

        if (this.target === mob) this.setTarget(null);
    }

    // ============================================================== mob AI
    _tickMobs(dt) {
        const st = this.state;
        const player = this.game.player;
        const now = performance.now();

        for (const mob of this.game.actors.mobs) {
            // Debuffs first: a slow that has run out must not still be slowing.
            if (mob.debuffs.length) {
                mob.debuffs = mob.debuffs.filter((d) => d.until > now);
            }

            if (mob.isDead) {
                mob.respawnIn -= dt;
                if (mob.respawnIn <= 0) mob.reset();
                continue;
            }

            const ctrl = mob.controller;
            const dist = distanceTo(ctrl, player.position.x, player.position.z);
            const home = Math.hypot(
                ctrl.position.x - mob.spawn.x, ctrl.position.z - mob.spawn.z
            );

            // Leash. Hostiles in this game used to follow you across the entire
            // map for ever; a mob that cannot be escaped is a mob that cannot be
            // fought twice.
            if (mob.state === "chase" && home > LEASH_RANGE) {
                mob.state = "return";
                mob.aggro = false;
                if (this.target === mob) this.autoAttack = false;
            }

            switch (mob.state) {
                case "return": {
                    ctrl.intent.faceAngle = null;
                    const arrived = seek(
                        ctrl, mob.spawn.x, mob.spawn.z, mob.moveSpeed * 1.2, 0.5
                    );
                    // Walking home heals it back up, so a mob you ran away from
                    // is a fresh fight rather than a free kill.
                    mob.hp = Math.min(mob.maxHp, mob.hp + mob.maxHp * 0.25 * dt);
                    if (arrived) mob.state = "idle";
                    break;
                }

                case "chase": {
                    ctrl.intent.faceAngle = angleTo(ctrl, player.position.x, player.position.z);
                    if (st.isDead) {
                        mob.state = "return";
                        break;
                    }
                    if (dist > MOB_RANGE) {
                        seek(ctrl, player.position.x, player.position.z,
                            mob.moveSpeed, MOB_RANGE - 0.3);
                    } else {
                        halt(ctrl);
                        mob.attackCooldown -= dt;
                        if (mob.attackCooldown <= 0) {
                            mob.attackCooldown = MOB_ATTACK_INTERVAL;
                            this._mobAttack(mob);
                        }
                    }
                    break;
                }

                default: {
                    // Idle: aggressive mobs notice you, everything else pads
                    // around its spawn so the camp is not a diorama.
                    ctrl.intent.faceAngle = null;
                    if (!st.isDead && mob.type === "aggressive" && dist < AGGRO_RANGE) {
                        mob.state = "chase";
                        mob.aggro = true;
                        st.addChat("System", "system", `${mob.name} snarls and charges!`, true);
                        break;
                    }
                    mob.wanderIn -= dt;
                    if (mob.wanderIn <= 0 && !mob.wanderTo) {
                        const a = Math.random() * Math.PI * 2;
                        const r = 2 + Math.random() * 6;
                        mob.wanderTo = {
                            x: mob.spawn.x + Math.sin(a) * r,
                            z: mob.spawn.z + Math.cos(a) * r,
                        };
                    }
                    if (mob.wanderTo) {
                        const done = seek(ctrl, mob.wanderTo.x, mob.wanderTo.z, 1.5);
                        if (done) {
                            mob.wanderTo = null;
                            mob.wanderIn = 3 + Math.random() * 8;
                        }
                    } else {
                        halt(ctrl);
                    }
                    break;
                }
            }
        }
    }

    _mobAttack(mob) {
        const st = this.state;
        const raw = Math.max(5, mob.data.patk * 1.5 - st.stats.pdef * 0.8);
        const damage = Math.max(
            1, Math.floor(raw * (0.9 + Math.random() * 0.2) * S.mobDamage)
        );

        // CP is a shield that soaks first, then HP.
        let remaining = damage;
        if (st.stats.cp > 0) {
            const absorbed = Math.min(st.stats.cp, remaining);
            st.stats.cp -= absorbed;
            remaining -= absorbed;
        }
        if (remaining > 0) st.stats.hp = Math.max(0, st.stats.hp - remaining);

        const p = this.game.player.position;
        st.addFloatingText(`-${damage}`, p.x, p.y + 1.9, p.z, COLOR_HURT);
        st.addChat(mob.name, "damage", `hits you for ${damage}.`, true);
        audio.playHurt();
        this.game.rig.addTrauma(0.06);

        if (st.stats.hp <= 0) this._playerDied();
        st.notify();
    }

    // ================================================================ death
    _playerDied() {
        const st = this.state;
        st.isDead = true;
        this.autoAttack = false;
        this.cancelCast(null);
        this.deathTimer = 0;
        st.applyDeathPenalty();
        st.buffs.length = 0;
        st.recalculateStats();
        st.system("You have died.");
        this.game.stopMoving();

        for (const mob of this.game.actors.mobs) {
            if (mob.state === "chase") mob.state = "return";
        }
        st.saveState();
        st.notify();
    }

    /** Stand the player back up at the village, at 40% health. */
    revive() {
        const st = this.state;
        if (!st.isDead) return;
        st.isDead = false;
        st.stats.hp = Math.floor(st.stats.maxHp * 0.4);
        st.stats.mp = Math.floor(st.stats.maxMp * 0.4);
        st.stats.cp = 0;
        this.game.teleport(0, 0);
        st.system("Restored at the village.");
        st.notify();
    }

    // =========================================================== regen
    _tickRegen(dt) {
        this.regenTimer += dt;
        if (this.regenTimer < REGEN_INTERVAL) return;
        this.regenTimer -= REGEN_INTERVAL;

        const st = this.state;
        const s = st.stats;
        const mult = st.isSitting ? 2.5 : 1;
        s.hp = Math.min(s.maxHp, s.hp + Math.floor(12 * mult));
        s.mp = Math.min(s.maxMp, s.mp + Math.floor(10 * mult));
        s.cp = Math.min(s.maxCp, s.cp + Math.floor(8 * mult));
        st.notify();
    }
}

/** Quest catalogue re-export, so `combat` is the only import a caller needs. */
export { QUESTS };
